/**
 * @module FleetDetailPanel
 * @description Right-side detail panel showing lane config, allocation status, active modules,
 * and action buttons. Supports allocate (with purpose + duration), release, and extend.
 * Fetches active module set from the lane via SSM when user expands the modules section.
 */
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, Package, RotateCcw, DownloadCloud } from 'lucide-react';
import { getTimeLeft, getLeaseDuration, laneWebUiUrl } from './fleetConstants';
import DeployStatusSection from './DeployStatusSection';
import { ModulesTable, useIssRestart, useIssModules } from './IssShared';
import SyncFromDeviceModal from './SyncFromDeviceModal';
import CopyButton from './CopyButton';

function FleetDetailPanel({ selectedLane, storeIss, currentUser, onRelease, onAllocate, onExtend, onRestart, onClose, actionLoading, fleetApi, deployWindowOpen, onUpdateLane }) {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [modulesExpandedForLane, setModulesExpandedForLane] = useState(null);
  const [modules, setModules] = useState(null);
  const [expectedModules, setExpectedModules] = useState(null);
  const [eleraVersion, setEleraVersion] = useState(null);
  const [osVersion, setOsVersion] = useState(null);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState(null);
  const [purpose, setPurpose] = useState('');
  const [duration, setDuration] = useState('');
  const [durationError, setDurationError] = useState('');
  const [restarting, setRestarting] = useState(false);
  const [restartMessage, setRestartMessage] = useState(null);
  const [rebooting, setRebooting] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const [checkingReachability, setCheckingReachability] = useState(false);
  const [unreachableReason, setUnreachableReason] = useState(null);

  const selectedWebUiUrl = laneWebUiUrl(selectedLane);

  // Reset state when the selected lane changes
  useEffect(() => {
    setDetailsExpanded(false);
    setShowSync(false);
    setModulesExpandedForLane(null);
    setModules(null);
    setExpectedModules(null);
    setEleraVersion(null);
    setOsVersion(null);
    setModulesError(null);
    setRestarting(false);
    setRestartMessage(null);
    setRebooting(false);
    setUnreachable(false);
    setCheckingReachability(false);
    setUnreachableReason(null);
  }, [selectedLane?.laneId]);

  // Probe reachability on panel open so the restart control starts disabled for
  // offline devices. A failed/uncertain probe is treated as reachable (fail-open).
  useEffect(() => {
    if (!selectedLane?.dsn || !fleetApi || !onRestart) return;
    let cancelled = false;
    setCheckingReachability(true);
    (async () => {
      try {
        const data = await fleetApi(`/fleet/lanes/${selectedLane.laneId}/status`);
        if (cancelled) return;
        if (data && data.reachable === false) {
          setUnreachable(true);
          setUnreachableReason(data.reason || null);
        } else {
          // Reachable (or indeterminate) — clear any prior unreachable flag.
          setUnreachable(false);
          setUnreachableReason(null);
        }
      } catch {
        // Probe failed — leave the control enabled (fail-open).
      } finally {
        if (!cancelled) setCheckingReachability(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedLane?.laneId, selectedLane?.dsn, fleetApi, onRestart]);

  // An offline/not-found modules error also marks the device unreachable.
  useEffect(() => {
    if (modulesError && /offline|not found in ssm|unreachable/i.test(modulesError)) {
      setUnreachable(true);
      setUnreachableReason(modulesError);
    }
  }, [modulesError]);

  // Fetch modules only when explicitly expanded for the current lane
  useEffect(() => {
    if (modulesExpandedForLane !== selectedLane?.laneId || !selectedLane?.dsn || !fleetApi) return;
    let cancelled = false;
    async function fetchModules() {
      setModulesLoading(true);
      setModulesError(null);
      setModules(null);
      setExpectedModules(null);
      setEleraVersion(null);
      setOsVersion(null);
      try {
        const data = await fleetApi(`/fleet/lanes/${selectedLane.laneId}/modules`);
        if (!cancelled) {
          setModules(data.modules);
          setExpectedModules(data.expectedModules || null);
          setEleraVersion(data.eleraVersion || null);
          setOsVersion(data.osVersion || null);
          // A successful modules read means the device is reachable now.
          setUnreachable(false);
          setUnreachableReason(null);
        }
      } catch (err) {
        if (!cancelled) setModulesError(err.message || 'Failed to fetch modules');
      } finally {
        if (!cancelled) setModulesLoading(false);
      }
    }
    fetchModules();
    return () => { cancelled = true; };
  }, [modulesExpandedForLane, selectedLane?.laneId, selectedLane?.dsn, fleetApi]);

  const handleRestart = async () => {
    if (!onRestart) return;
    const confirmed = window.confirm(
      `Restart ${selectedLane.hardwareId || selectedLane.laneId}?\n\nThe device will reboot and be unavailable for a few minutes.`
    );
    if (!confirmed) return;
    setRestarting(true);
    setRestartMessage(null);
    try {
      await onRestart(selectedLane.laneId);
      // Reboot accepted — enter the "rebooting" state and show a message that
      // persists until the device comes back online (see the poll effect below).
      setRebooting(true);
      setRestartMessage({ type: 'info', text: 'Device rebooting — this takes a few minutes. This message clears once it is back online.' });
    } catch (err) {
      const msg = err.message || 'Failed to restart the device.';
      if (/offline|not found in ssm|unreachable/i.test(msg)) {
        setUnreachable(true);
        setUnreachableReason(msg);
      }
      setRestartMessage({ type: 'error', text: msg });
    } finally {
      setRestarting(false);
    }
  };

  // While rebooting, poll the reachability probe until the device is back
  // online, then clear the "Device rebooting" message. Starts after a delay so
  // we don't catch the device still up in the ~1s before it goes down.
  useEffect(() => {
    if (!rebooting || !selectedLane?.dsn || !fleetApi) return;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        // Poll /modules (not /status): a successful modules read means the
        // device is truly back up and its modules are loadable — SSM ping
        // (/status) can report Online before the module stack is serving.
        const data = await fleetApi(`/fleet/lanes/${selectedLane.laneId}/modules`);
        if (!cancelled && data && data.modules) {
          setRebooting(false);
          setRestartMessage(null);
          setUnreachable(false);
          setUnreachableReason(null);
        }
      } catch {
        // Device still down / modules not yet loadable — keep polling.
      }
    }, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [rebooting, selectedLane?.laneId, selectedLane?.dsn, fleetApi]);

  const handleAllocate = () => {
    let durationSeconds = null;
    /**
     * Max lease comes from the backend (maxLeaseSeconds on the lane) so the cap
     * lives in one place — utils/automation-lanes.js. Fall back to the known
     * defaults (8h automation / 90m normal) only if an older API omits it.
     */
    const maxSeconds = selectedLane.maxLeaseSeconds || (selectedLane.automationLane ? 8 * 3600 : 90 * 60);
    const maxMinutes = maxSeconds / 60;
    const maxLabel = maxMinutes % 60 === 0 ? `${maxMinutes / 60}h` : `${maxMinutes}m`;
    if (duration) {
      const trimmed = duration.trim().toLowerCase();
      let minutes;
      if (trimmed.endsWith('h')) {
        minutes = parseFloat(trimmed) * 60;
      } else if (trimmed.endsWith('m')) {
        minutes = parseFloat(trimmed);
      } else {
        minutes = parseFloat(trimmed);
      }
      if (isNaN(minutes) || minutes < 30 || minutes > maxMinutes) {
        setDurationError(`Duration must be between 30m and ${maxLabel}`);
        return;
      }
      durationSeconds = minutes * 60;
    }
    setDurationError('');
    onAllocate(selectedLane.laneId, purpose, durationSeconds);
    setPurpose('');
    setDuration('');
  };

  // --- ISS restart (reuses the same lane restart endpoint against the ISS lane row) ---
  const { restarting: issRestarting, message: issRestartMessage, restart: handleIssRestart } = useIssRestart(onRestart, storeIss);

  // --- ISS Active Modules (reuses /fleet/lanes/{laneId}/modules against the ISS lane row) ---
  const [issModulesExpanded, setIssModulesExpanded] = useState(false);

  // Collapse the ISS modules section when the selected store's ISS changes.
  useEffect(() => {
    setIssModulesExpanded(false);
  }, [storeIss?.laneId]);

  // Fetch ISS modules only while the section is expanded (pass a null laneId to
  // the hook when collapsed so it skips the fetch).
  const {
    modules: issModules, expectedModules: issExpectedModules,
    eleraVersion: issEleraVersion, osVersion: issOsVersion,
    loading: issModulesLoading, error: issModulesError,
  } = useIssModules(issModulesExpanded ? storeIss?.laneId : null, storeIss?.dsn, fleetApi);

  // Disabled while rebooting, restarting, probing, or unreachable — with a matching hint.
  const restartDisabled = restarting || rebooting || unreachable || checkingReachability || actionLoading;
  const restartHint = rebooting
    ? 'Device rebooting…'
    : restarting
      ? 'Sending reboot…'
      : checkingReachability
        ? 'Checking device status…'
        : unreachable
          ? (unreachableReason ? `Device is unreachable — ${unreachableReason}` : 'Device is unreachable — cannot reboot')
          : 'Reboot the device';
  // Concise label for the button face (full detail stays in the title tooltip).
  const restartLabel = rebooting
    ? 'Rebooting…'
    : restarting
      ? 'Rebooting…'
      : checkingReachability
        ? 'Checking…'
        : unreachable
          ? 'Unreachable'
          : 'Reboot the device';

  return (
    <div className="fleet-detail-panel">
      <button className="fleet-detail-close" onClick={onClose}>
        <ArrowLeft size={16} /> Back
      </button>
      <div className="fleet-detail-title-row">
        <h3>{selectedLane.hardwareId || selectedLane.laneId}</h3>
        {selectedLane.dsn && onUpdateLane && (
          <button
            className="fleet-restart-btn"
            onClick={() => setShowSync(true)}
            disabled={selectedLane.status === 'IN_USE'}
            title={selectedLane.status === 'IN_USE'
              ? 'Release the lane before syncing — an in-use lane cannot be edited'
              : "Read this device via SSM and update the lane's Device ID and IP if they have drifted"}
            aria-label="Sync from device"
          >
            <DownloadCloud size={14} />
            <span>Sync from device</span>
          </button>
        )}
        {selectedLane.dsn && onRestart && (
          <button
            className={`fleet-restart-btn ${unreachable ? 'fleet-restart-unreachable' : ''}`}
            onClick={handleRestart}
            disabled={restartDisabled}
            title={restartHint}
            aria-label={restartHint}
          >
            <RotateCcw size={14} className={restarting || rebooting || checkingReachability ? 'fleet-restart-spinning' : ''} />
            <span>{restartLabel}</span>
          </button>
        )}
      </div>
      {showSync && (
        <SyncFromDeviceModal
          lane={selectedLane}
          fleetApi={fleetApi}
          onApply={onUpdateLane}
          onClose={() => setShowSync(false)}
        />
      )}
      {restartMessage && (
        <div className={`fleet-restart-message ${restartMessage.type === 'error' ? 'fleet-restart-error' : 'fleet-restart-success'}`}>
          {restartMessage.type === 'error' ? '⚠ ' : '🔄 '}{restartMessage.text}
        </div>
      )}
      <div className={`fleet-detail-status ${selectedLane.status === 'FREE' ? 'detail-free' : 'detail-busy'}`}>
        {selectedLane.status === 'FREE' ? '🟢 Available' : '🟠 In Use'}
      </div>
      {selectedLane.reserved && (
        <div className="fleet-reserved-badge">🔒 Reserved for automation — not claimable</div>
      )}
      <div className="fleet-detail-toggle" onClick={() => setDetailsExpanded(prev => !prev)}>
        {detailsExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Lane Details</span>
      </div>
      {detailsExpanded && (
      <table className="fleet-detail-table">
        <tbody>
          <tr><td>Type</td><td>{selectedLane.laneType}</td></tr>
          <tr><td>Stage</td><td>{selectedLane.deploymentStage || '—'}</td></tr>
          <tr><td>Store ID</td><td>{selectedLane.storeId || '—'}</td></tr>
          <tr><td>Device ID</td><td>{selectedLane.deviceId || '—'}</td></tr>
          <tr><td>IP Address</td><td>{selectedLane.ipAddress || '—'}</td></tr>
          <tr><td>Port</td><td>{selectedLane.port || '—'}</td></tr>
          <tr><td>DSN</td><td>{selectedLane.dsn || '—'}</td></tr>
          <tr><td>Peripherals</td><td>{selectedLane.peripherals || '—'}</td></tr>
          <tr><td>Enterprise</td><td>{selectedLane.enterpriseConfig || '—'}</td></tr>
          {selectedLane.enterpriseUrl && (
            <tr><td>Enterprise URL</td><td><a href={selectedLane.enterpriseUrl} target="_blank" rel="noreferrer">{selectedLane.enterpriseUrl}</a></td></tr>
          )}
          {storeIss && (
            <tr>
              <td>ISS</td>
              <td>
                <div className="fleet-iss-row">
                  <a href={laneWebUiUrl(storeIss)} target="_blank" rel="noreferrer">{storeIss.ipAddress}:{storeIss.port}</a>
                  {storeIss.dsn && onRestart && (
                    <button
                      type="button"
                      className="fleet-restart-btn fleet-iss-restart-btn"
                      onClick={handleIssRestart}
                      disabled={issRestarting || actionLoading}
                      title={issRestarting ? 'Sending reboot…' : 'Reboot the ISS'}
                      aria-label="Restart ISS"
                    >
                      <RotateCcw size={12} className={issRestarting ? 'fleet-restart-spinning' : ''} />
                      <span>{issRestarting ? 'Rebooting…' : 'Restart'}</span>
                    </button>
                  )}
                </div>
                {issRestartMessage && (
                  <div className={`fleet-restart-message ${issRestartMessage.type === 'error' ? 'fleet-restart-error' : 'fleet-restart-success'}`}>
                    {issRestartMessage.type === 'error' ? '⚠ ' : '🔄 '}{issRestartMessage.text}
                  </div>
                )}
              </td>
            </tr>
          )}
          {(selectedLane.adminUiUrl || selectedLane.enterpriseUrl) && (
            <tr><td>Admin UI</td><td><a href={selectedLane.adminUiUrl || (selectedLane.enterpriseUrl + '/admin-ui/')} target="_blank" rel="noreferrer">Open Admin UI</a></td></tr>
          )}
          {selectedWebUiUrl && (
            <tr><td>Web UI</td><td><a href={selectedWebUiUrl} target="_blank" rel="noreferrer">Open POS UI</a></td></tr>
          )}
          <tr><td>Lab</td><td>{selectedLane.lab || '—'}</td></tr>
          {(selectedLane.gammaLibby || storeIss?.gammaLibby) && (
            <tr><td>Libby ID</td><td>{selectedLane.gammaLibby || storeIss?.gammaLibby}</td></tr>
          )}
          {selectedLane.status === 'IN_USE' && (
            <>
              <tr><td>Allocated By</td><td><strong>{selectedLane.allocatedBy}</strong></td></tr>
              <tr><td>Since</td><td>{new Date(selectedLane.allocatedAt).toLocaleString()}</td></tr>
              <tr><td>Purpose</td><td>{selectedLane.purpose || '—'}</td></tr>
            </>
          )}
          {selectedLane.lastReleasedBy && selectedLane.status === 'FREE' && (
            <tr><td>Last Used By</td><td>{selectedLane.lastReleasedBy} ({new Date(selectedLane.lastReleasedAt).toLocaleString()})</td></tr>
          )}
        </tbody>
      </table>
      )}

      {selectedLane.dsn && (
        <>
          <div className="fleet-detail-toggle" onClick={() => setModulesExpandedForLane(prev => prev ? null : selectedLane.laneId)}>
            {modulesExpandedForLane === selectedLane?.laneId ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Package size={14} />
            <span>Active Modules</span>
          </div>
          {modulesExpandedForLane === selectedLane?.laneId && (
            <div className="fleet-modules-section">
              {modulesLoading && (
                <div className="fleet-modules-loading">
                  <span className="fleet-modules-spinner" />
                  <div>
                    <div>Connecting to device via SSM...</div>
                    <div className="fleet-modules-loading-hint">This may take 5–10 seconds</div>
                  </div>
                </div>
              )}
              {modulesError && (
                <div className="fleet-modules-error">⚠ {modulesError}<div className="fleet-modules-error-hint">The device may be offline or unreachable. Try again later.</div></div>
              )}
              {!modulesLoading && eleraVersion && (
                <div className="fleet-platform-version">Elera Version: <span className="fleet-version-value">{eleraVersion}</span><CopyButton value={eleraVersion} /></div>
              )}
              {!modulesLoading && osVersion && (
                <div className="fleet-platform-version">Majestic OS Version: <span className="fleet-version-value">{osVersion}</span><CopyButton value={osVersion} /></div>
              )}
              {!modulesLoading && (
                <ModulesTable modules={modules} expectedModules={expectedModules} stage={selectedLane.deploymentStage} />
              )}
            </div>
          )}
        </>
      )}

      <DeployStatusSection laneId={selectedLane.laneId} dsn={selectedLane.dsn} open={deployWindowOpen} fleetApi={fleetApi} />

      {storeIss && storeIss.dsn && (
        <>
          <div className="fleet-detail-toggle" onClick={() => setIssModulesExpanded(prev => !prev)}>
            {issModulesExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <Package size={14} />
            <span>ISS Active Modules</span>
          </div>
          {issModulesExpanded && (
            <div className="fleet-modules-section">
              {issModulesLoading && (
                <div className="fleet-modules-loading">
                  <span className="fleet-modules-spinner" />
                  <div>
                    <div>Connecting to ISS via SSM...</div>
                    <div className="fleet-modules-loading-hint">This may take 5–10 seconds</div>
                  </div>
                </div>
              )}
              {issModulesError && (
                <div className="fleet-modules-error">⚠ {issModulesError}<div className="fleet-modules-error-hint">The ISS may be offline or unreachable. Try again later.</div></div>
              )}
              {!issModulesLoading && issEleraVersion && (
                <div className="fleet-platform-version">Elera Version: <span className="fleet-version-value">{issEleraVersion}</span><CopyButton value={issEleraVersion} /></div>
              )}
              {!issModulesLoading && issOsVersion && (
                <div className="fleet-platform-version">Majestic OS Version: <span className="fleet-version-value">{issOsVersion}</span><CopyButton value={issOsVersion} /></div>
              )}
              {!issModulesLoading && (
                <ModulesTable
                  modules={issModules}
                  expectedModules={issExpectedModules}
                  stage={storeIss.deploymentStage}
                  emptyMessage="No modules found on the ISS."
                />
              )}
            </div>
          )}
        </>
      )}

      {selectedLane.status === 'FREE' && (
        <div className="fleet-action">
          <input
            type="text"
            placeholder="Purpose (e.g., Testing BRD-1234 tender flows)"
            value={purpose}
            onChange={e => setPurpose(e.target.value)}
            className="fleet-purpose-input"
          />
          <input
            type="text"
            placeholder={selectedLane.automationLane ? 'Duration (optional, default 8h; e.g. 4h, 8h)' : 'Duration (optional, e.g. 30m, 1h, 1.5h)'}
            value={duration}
            onChange={e => { setDuration(e.target.value); setDurationError(''); }}
            className="fleet-purpose-input"
          />
          {durationError && <span className="fleet-duration-error">{durationError}</span>}
          <button
            className="btn btn-primary"
            onClick={handleAllocate}
            disabled={actionLoading || selectedLane.reserved}
            title={selectedLane.reserved ? 'Reserved for automation — this lane cannot be claimed' : undefined}
          >
            {selectedLane.reserved
              ? 'Reserved for automation'
              : actionLoading ? 'Allocating...' : `Allocate Lane (Default ${selectedLane.automationLane ? '8h' : '30m'})`}
          </button>
        </div>
      )}

      {selectedLane.status === 'IN_USE' && (selectedLane.allocatedBy === currentUser || selectedLane.allocatedBy === currentUser + '@amazon.com') && (
        <div className="fleet-action">
          {selectedLane.allocatedAt && getTimeLeft(selectedLane.allocatedAt, selectedLane.leaseDuration) !== 'expired' && (
            <div className="fleet-timer-display">⏱ {getTimeLeft(selectedLane.allocatedAt, selectedLane.leaseDuration)}</div>
          )}
          {selectedLane.allocatedAt && (() => {
            const remaining = getLeaseDuration(selectedLane) - (Date.now() - new Date(selectedLane.allocatedAt).getTime());
            return remaining < getLeaseDuration(selectedLane) * 0.7 && remaining > 0;
          })() && (
            <div className="fleet-extend-options">
              <span className="fleet-extend-label">Extend for:</span>
              <button className="btn btn-sm btn-primary" onClick={() => onExtend(selectedLane.laneId, 1800)} disabled={actionLoading}>30m</button>
              <button className="btn btn-sm btn-primary" onClick={() => onExtend(selectedLane.laneId, 3600)} disabled={actionLoading}>1h</button>
              <button className="btn btn-sm btn-primary" onClick={() => onExtend(selectedLane.laneId, 7200)} disabled={actionLoading}>2h</button>
            </div>
          )}
          <button
            className="btn btn-danger"
            onClick={() => onRelease(selectedLane.laneId)}
            disabled={actionLoading}
          >
            {actionLoading ? 'Releasing...' : 'Release Lane'}
          </button>
        </div>
      )}
    </div>
  );
}

export default FleetDetailPanel;
